import types from '../../mutation-types';
import ConversationApi from '../../../api/inbox/conversation';
import MessageApi from '../../../api/inbox/message';
import AccountActionsApi from '../../../api/accountActions';

import { MESSAGE_STATUS, MESSAGE_TYPE } from 'shared/constants/messages';
import { createPendingMessage } from 'dashboard/helper/commons';
import vueActionCable from '../../../helper/actionCable';
import {
  buildConversationList,
  isOnMentionsView,
  isOnUnattendedView,
  isOnFoldersView,
} from './helpers/actionHelpers';
import messageReadActions from './actions/messageReadActions';
import messageTranslateActions from './actions/messageTranslateActions';
import * as Sentry from '@sentry/browser';
import {
  MAX_SUBSCRIBED_CONVERSATIONS,
  SUBSCRIPTION_CONFIRM_TIMEOUT_MS,
  MAX_SUBSCRIPTION_RETRIES,
  compareMessagesByCreatedAt,
} from './helpers';

export const hasMessageFailedWithExternalError = pendingMessage => {
  // This helper is used to check if the message has failed with an external error.
  // We have two cases
  // 1. Messages that fail from the UI itself (due to large attachments or a failed network):
  //    In this case, the message will have a status of failed but no external error. So we need to create that message again
  // 2. Messages sent from Chatwoot but failed to deliver to the customer for some reason (user blocking or client system down):
  //    In this case, the message will have a status of failed and an external error. So we need to retry that message
  const { content_attributes: contentAttributes, status } = pendingMessage;
  const externalError = contentAttributes?.external_error ?? '';
  return status === MESSAGE_STATUS.FAILED && externalError !== '';
};

// A tab/inbox/filter switch cancels an in-flight request — routine, not an
// incident, and never the cancelled request's state to clean up. axios 1.x throws
// CanceledError/ERR_CANCELED; AbortError covers a non-axios abort.
const isRequestCancellation = error =>
  error?.code === 'ERR_CANCELED' ||
  error?.name === 'CanceledError' ||
  error?.name === 'AbortError';

// actions
const actions = {
  getConversation: async ({ commit }, conversationId) => {
    try {
      const response = await ConversationApi.show(conversationId);
      commit(types.UPDATE_CONVERSATION, response.data);
      commit(`contacts/${types.SET_CONTACT_ITEM}`, response.data.meta.sender);
      return response.data;
    } catch (error) {
      // Ignore error
      return null;
    }
  },

  // Commits nothing on purpose: the caller only needs the display_id to reach the canonical
  // url, and the conversation is then loaded by getConversation like any other route.
  // Returns null on 403/404 so a uuid nobody may open lands on the empty state.
  resolveConversationUuid: async (_store, uuid) => {
    try {
      const { data } = await ConversationApi.lookupByUuid(uuid);
      return data.display_id;
    } catch (error) {
      return null;
    }
  },

  fetchAllConversations: async (
    { commit, state, dispatch },
    { replace = false, signal } = {}
  ) => {
    commit(types.SET_LIST_LOADING_STATUS);
    try {
      const params = state.conversationFilters;
      // With a signal, an aborted request rejects here and nothing is committed —
      // otherwise a superseded scope's response would replace the current list.
      const {
        data: { data },
      } = await ConversationApi.get({ ...params, signal });
      // axios can't reject a response that already resolved, so re-check before
      // applying: replacing the list with a superseded scope is the worst outcome.
      // Deliberately does NOT clear the loading flag: we were superseded, so a
      // newer fetch owns it — clearing here would erase that one's spinner.
      if (signal?.aborted) return false;
      buildConversationList(
        { commit, dispatch },
        params,
        data,
        params.assigneeType,
        { replace }
      );
      // Returned so callers can tell a load that landed from one that didn't.
      // The loading flag is UI state (spinner, load-more gate) and must be
      // cleared on every exit — it is not a success signal.
      return true;
    } catch (error) {
      // Cancellation means a newer fetch superseded us and owns the loading flag;
      // clearing it here would erase that one's spinner and unblock load-more
      // mid-load. Only a real failure releases it.
      if (!isRequestCancellation(error)) {
        commit(types.CLEAR_LIST_LOADING_STATUS);
      }
      return false;
    }
  },

  // Me-tab self-heal (AB#8067): a bounded updated_within fetch that MERGES
  // recently-changed conversations and refreshes stats. Used when the counter
  // poll reveals the list is behind (a dropped assignee/status event). It
  // deliberately does NOT go through buildConversationList: that path calls
  // setPageFilter (markEndReached: length < 25), which a small updated_within
  // result would wrongly trip, breaking "load more". Count stays authoritative
  // because mine_count is computed before updated_within narrows the scope
  // (ConversationFinder#perform).
  reconcileMineConversations: async (
    { commit, dispatch, state },
    { updatedWithin, signal }
  ) => {
    const params = {
      ...state.conversationFilters,
      page: undefined,
      updatedWithin,
    };
    let response;
    try {
      response = await ConversationApi.get({ ...params, signal });
    } catch (error) {
      if (!isRequestCancellation(error)) {
        // Otherwise a broken endpoint just reads as "the list is still stale",
        // with no trace anywhere.
        Sentry.setContext('Conversation reconcile', {
          updatedWithin,
          assigneeType: params.assigneeType,
          status: params.status,
          inboxId: params.inboxId,
        });
        Sentry.captureException(error);
      }
      // Rethrow: the caller must not advance its baseline on a failure.
      throw error;
    }
    const {
      data: {
        data: { payload, meta },
      },
    } = response;
    // A tab/inbox/filter switch aborts the generation that requested this heal;
    // drop a response scoped to the old selection instead of merging it.
    if (signal?.aborted) return;
    commit(types.SET_ALL_CONVERSATION, payload);
    // Per-item, NOT setContacts: SET_CONTACTS overwrites `sortOrder`, so a heal
    // would shrink the Contacts list to just these senders (see the note on
    // SET_CONTACT_ITEM in contacts/mutations.js).
    payload.forEach(chat => {
      const sender = chat.meta?.sender;
      if (sender) {
        commit(`contacts/${types.SET_CONTACT_ITEM}`, sender);
      }
    });
    dispatch('conversationStats/set', meta);
  },

  fetchFilteredConversations: async ({ commit, dispatch }, params) => {
    commit(types.SET_LIST_LOADING_STATUS);
    try {
      const { data } = await ConversationApi.filter(params);
      buildConversationList(
        { commit, dispatch },
        params,
        data,
        'appliedFilters'
      );
    } catch (error) {
      // Handle error
    }
  },

  emptyAllConversations({ commit }) {
    commit(types.EMPTY_ALL_CONVERSATION);
  },

  clearSelectedState({ commit }) {
    commit(types.CLEAR_CURRENT_CHAT_WINDOW);
  },

  fetchPreviousMessages: async ({ commit }, data) => {
    try {
      const {
        data: { meta, payload },
      } = await MessageApi.getPreviousMessages(data);
      commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
        id: data.conversationId,
        data: meta,
      });
      commit(types.SET_PREVIOUS_CONVERSATIONS, {
        id: data.conversationId,
        data: payload,
      });
      if (!payload.length) {
        commit(types.SET_ALL_MESSAGES_LOADED, data.conversationId);
      }
    } catch (error) {
      Sentry.setContext('Conversation', {
        id: data.conversationId,
        before: data.before,
      });
      Sentry.captureException(error);
      throw error;
    }
  },

  fetchAllAttachments: async ({ commit }, conversationId) => {
    let attachments = null;

    try {
      const { data } = await ConversationApi.getAllAttachments(conversationId);
      attachments = data.payload;
    } catch (error) {
      // in case of error, log the error and continue
      Sentry.setContext('Conversation', {
        id: conversationId,
      });
      Sentry.captureException(error);
    } finally {
      // we run the commit even if the request fails
      // this ensures that the `attachment` variable is always present on chat
      commit(types.SET_ALL_ATTACHMENTS, {
        id: conversationId,
        data: attachments,
      });
    }
  },

  syncActiveConversationMessages: async (
    { commit, state, dispatch },
    { conversationId }
  ) => {
    const { allConversations, syncConversationsMessages } = state;
    const lastMessageId = syncConversationsMessages[conversationId];
    const selectedChat = allConversations.find(
      conversation => conversation.id === conversationId
    );
    if (!selectedChat) return;
    try {
      // Fetch all the messages after the last message id
      const {
        data: { meta, payload },
      } = await MessageApi.getPreviousMessages({
        conversationId,
        after: lastMessageId,
      });
      commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
        id: conversationId,
        data: meta,
      });
      // Read the thread AFTER the await. This runs for the OPEN conversation:
      // read from the reactive currentConversation, not the frozen list row
      // (which may hold a degraded/capped set). cloneConversationForCurrent
      // allocates a fresh messages array on every rebuild, so a pre-await
      // snapshot would clobber messages appended (ADD_MESSAGE) during the fetch.
      const liveChat =
        state.allConversations.find(c => c.id === conversationId) ||
        selectedChat;
      const messages =
        state.currentConversationId === conversationId &&
        state.currentConversation
          ? state.currentConversation.messages
          : liveChat.messages;
      // Find the messages that are not already present in the store
      const missingMessages = payload.filter(
        message => !messages.find(item => item.id === message.id)
      );
      // Build a fresh, sorted array — selectedChat is a frozen list row, so it
      // must not be mutated in place. SET_MISSING_MESSAGES replaces the slot.
      const sortedMessages = [...messages, ...missingMessages].sort(
        compareMessagesByCreatedAt
      );
      commit(types.SET_MISSING_MESSAGES, {
        id: conversationId,
        data: sortedMessages,
      });
      commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
        conversationId,
        messageId: null,
      });
      dispatch('markMessagesRead', { id: conversationId }, { root: true });
    } catch (error) {
      // Handle error
    }
  },

  setConversationLastMessageId: async (
    { commit, state },
    { conversationId }
  ) => {
    const { allConversations } = state;
    const selectedChat = allConversations.find(
      conversation => conversation.id === conversationId
    );
    if (!selectedChat) return;
    const { messages } = selectedChat;
    const lastMessage = messages.last();
    if (!lastMessage) return;
    commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
      conversationId,
      messageId: lastMessage.id,
    });
  },

  async setActiveChat(
    { commit, dispatch, rootGetters, state },
    { data, after }
  ) {
    commit(types.SET_CURRENT_CHAT_WINDOW, data);
    commit(types.CLEAR_ALL_MESSAGES_LOADED);

    const currentUser = rootGetters.getCurrentUser;
    const pubsubToken = rootGetters.getCurrentUserPubsubToken;
    const assignee = data.meta?.assignee;
    const isNotAssignee = !assignee || assignee.id !== currentUser.id;
    const conversationId = data.id;

    const conversationToRemove =
      state.subscribedNonAssignedConversations.length >=
      MAX_SUBSCRIBED_CONVERSATIONS
        ? state.subscribedNonAssignedConversations[0]
        : null;

    // SET_CURRENT_CHAT_WINDOW cloned `data` into the reactive currentConversation.
    // Read fetch state from there — `data` may be a frozen list row (it comes
    // from getConversationById in ChatList/InboxView) which silently rejects
    // writes, and its messages array no longer grows with the thread. The flag is
    // written back to both copies via SET_CONVERSATION_DATA_FETCHED, so opening
    // the same conversation again does not refetch the history.
    const activeConversation = state.currentConversation;
    if (activeConversation && activeConversation.dataFetched === undefined) {
      try {
        const newestMessage = (activeConversation.messages || [])[0];
        // First attempt: fetch older messages using `before=<newest id>`.
        await dispatch('fetchPreviousMessages', {
          after,
          before: newestMessage?.id,
          conversationId,
        });
        // The awaits yield control; if the user opened another conversation
        // meanwhile, currentConversation now points at it. Only keep touching
        // fetch state while `conversationId` is still the open chat, so we never
        // seed dataFetched (or fire an extra fetch) onto the wrong conversation.
        // If still only one message, there were no older messages; try fetching newer ones via `after`.
        if (
          state.currentConversationId === conversationId &&
          (state.currentConversation.messages || []).length === 1 &&
          newestMessage
        ) {
          await dispatch('fetchPreviousMessages', {
            conversationId,
            after: newestMessage.id,
          });
        }
        if (state.currentConversationId === conversationId) {
          commit(types.SET_CONVERSATION_DATA_FETCHED, conversationId);
        }
      } catch (error) {
        // Ignore error
      }
    }

    if (isNotAssignee && pubsubToken) {
      dispatch('subscribeToConversation', {
        conversationId: conversationId,
        pubsubToken,
        conversationOldestId: conversationToRemove,
      });
    }
  },

  assignAgent: async (
    { dispatch, rootGetters },
    { conversationId, agentId }
  ) => {
    const response = await ConversationApi.assignAgent({
      conversationId,
      agentId,
    });
    dispatch('setCurrentChatAssignee', response.data);

    const pubsubToken = rootGetters?.getCurrentUserPubsubToken;
    const currentUserId = rootGetters?.getCurrentUserID;
    const assignedAgentId = response.data && response.data.id;

    if (pubsubToken && assignedAgentId !== currentUserId) {
      dispatch('subscribeToConversation', { conversationId, pubsubToken });
    }
  },

  setCurrentChatAssignee({ commit }, assignee) {
    commit(types.ASSIGN_AGENT, assignee);
  },

  assignTeam: async (
    { dispatch },
    { conversationId, teamId, aiHandoffConfirmed }
  ) => {
    const response = await ConversationApi.assignTeam({
      conversationId,
      teamId,
      aiHandoffConfirmed,
    });
    dispatch('setCurrentChatTeam', { team: response.data, conversationId });
  },

  setCurrentChatTeam({ commit }, { team, conversationId }) {
    commit(types.ASSIGN_TEAM, { team, conversationId });
  },

  toggleStatus: async (
    { commit },
    { conversationId, status, snoozedUntil = null }
  ) => {
    // eslint-disable-next-line no-useless-catch
    try {
      const {
        data: {
          payload: {
            current_status: updatedStatus,
            snoozed_until: updatedSnoozedUntil,
          } = {},
        } = {},
      } = await ConversationApi.toggleStatus({
        conversationId,
        status,
        snoozedUntil,
      });
      commit(types.CHANGE_CONVERSATION_STATUS, {
        conversationId,
        status: updatedStatus,
        snoozedUntil: updatedSnoozedUntil,
      });
    } catch (error) {
      throw error;
    }
  },

  mergeConversations: async (
    { commit },
    { targetConversationId, sourceConversationIds, transitionMessages }
  ) => {
    const { data } = await AccountActionsApi.mergeConversations({
      targetConversationId,
      sourceConversationIds,
      transitionMessages,
    });
    commit(types.MERGE_CONVERSATIONS, data);
    return data;
  },

  // F9: cross-agent real-time sync — dispatched by the actionCable
  // `conversation.merged` handler for every agent OTHER than the one who
  // initiated the merge (that agent already got MERGE_CONVERSATIONS above,
  // from the mergeConversations action's own response).
  conversationMergedRealtime(
    { commit, dispatch },
    { sourceIds, survivorId, contactId }
  ) {
    commit(types.CONVERSATION_MERGED_REALTIME, { sourceIds, survivorId });

    // Same-contact merge — also patch the contactConversations cache (the
    // "Previous Conversations" panel + its Merged badge) so it doesn't stay
    // stale until the panel is manually refetched. `contactConversations` is
    // its own namespaced module — dispatch works unprefixed here the same
    // way `contacts/setContact` does elsewhere in this file, since this
    // (conversations) module itself isn't namespaced.
    if (contactId) {
      dispatch('contactConversations/mergeRealtime', {
        contactId,
        sourceIds,
        survivorId,
      });
    }
  },

  createPendingMessageAndSend: async ({ dispatch }, data) => {
    const pendingMessage = createPendingMessage(data);
    return dispatch('sendMessageWithData', pendingMessage);
  },

  sendMessageWithData: async ({ commit }, pendingMessage) => {
    const { conversation_id: conversationId, id } = pendingMessage;
    try {
      commit(types.ADD_MESSAGE, {
        ...pendingMessage,
        status: MESSAGE_STATUS.PROGRESS,
      });
      const response = hasMessageFailedWithExternalError(pendingMessage)
        ? await MessageApi.retry(conversationId, id)
        : await MessageApi.create(pendingMessage);
      commit(types.ADD_MESSAGE, {
        ...response.data,
        status: MESSAGE_STATUS.SENT,
      });
      commit(types.ADD_CONVERSATION_ATTACHMENTS, {
        ...response.data,
        status: MESSAGE_STATUS.SENT,
      });
    } catch (error) {
      const errorMessage = error.response
        ? error.response.data.error
        : undefined;
      commit(types.ADD_MESSAGE, {
        ...pendingMessage,
        meta: {
          error: errorMessage,
        },
        status: MESSAGE_STATUS.FAILED,
      });
      throw error;
    }
  },

  addMessage({ commit }, message) {
    commit(types.ADD_MESSAGE, message);
    if (message.message_type === MESSAGE_TYPE.INCOMING) {
      commit(types.SET_CONVERSATION_CAN_REPLY, {
        conversationId: message.conversation_id,
        canReply: true,
      });
      commit(types.ADD_CONVERSATION_ATTACHMENTS, message);
    }
  },

  updateMessage({ commit }, message) {
    commit(types.ADD_MESSAGE, message);
  },

  deleteMessage: async function deleteLabels(
    { commit },
    { conversationId, messageId }
  ) {
    try {
      const { data } = await MessageApi.delete(conversationId, messageId);
      commit(types.ADD_MESSAGE, data);
      commit(types.DELETE_CONVERSATION_ATTACHMENTS, data);
    } catch (error) {
      throw new Error(error);
    }
  },

  addConversation({ commit, state, dispatch, rootState }, conversation) {
    const { currentInbox, appliedFilters } = state;
    const {
      inbox_id: inboxId,
      meta: { sender },
    } = conversation;
    const hasAppliedFilters = !!appliedFilters.length;
    const isMatchingInboxFilter =
      !currentInbox || Number(currentInbox) === inboxId;
    if (
      !hasAppliedFilters &&
      !isOnFoldersView(rootState) &&
      !isOnMentionsView(rootState) &&
      !isOnUnattendedView(rootState) &&
      isMatchingInboxFilter
    ) {
      commit(types.ADD_CONVERSATION, conversation);
      dispatch('contacts/setContact', sender);
    }
  },

  addMentions({ dispatch, rootState }, conversation) {
    if (isOnMentionsView(rootState)) {
      dispatch('updateConversation', conversation);
    }
  },

  addUnattended({ dispatch, rootState }, conversation) {
    if (isOnUnattendedView(rootState)) {
      dispatch('updateConversation', conversation);
    }
  },

  updateConversation({ commit, dispatch, state }, conversation) {
    const sender = conversation.meta?.sender;
    // Delta events (payloads without meta, e.g. conversation.read) carry only
    // the changed fields: they can annotate conversations already in the
    // store, but there is not enough data to render a new list entry.
    if (!conversation.meta) {
      const isKnown =
        state.conversationIndexById instanceof Map
          ? state.conversationIndexById.has(conversation.id)
          : state.allConversations.some(c => c.id === conversation.id);
      if (!isKnown) return;
    }
    commit(types.UPDATE_CONVERSATION, conversation);

    if (conversation.labels) {
      dispatch('conversationLabels/setConversationLabel', {
        id: conversation.id,
        data: conversation.labels,
      });
    }

    if (sender) dispatch('contacts/setContact', sender);
  },

  updateConversationLastActivity(
    { commit },
    { conversationId, lastActivityAt }
  ) {
    commit(types.UPDATE_CONVERSATION_LAST_ACTIVITY, {
      lastActivityAt,
      conversationId,
    });
  },

  setChatStatusFilter({ commit }, data) {
    commit(types.CHANGE_CHAT_STATUS_FILTER, data);
  },

  setChatSortFilter({ commit }, data) {
    commit(types.CHANGE_CHAT_SORT_FILTER, data);
  },

  updateAssignee({ commit }, data) {
    commit(types.UPDATE_ASSIGNEE, data);
  },

  updateConversationContact({ commit }, data) {
    if (data.id) {
      commit(`contacts/${types.SET_CONTACT_ITEM}`, data);
    }
    commit(types.UPDATE_CONVERSATION_CONTACT, data);
  },

  setActiveInbox({ commit }, inboxId) {
    commit(types.SET_ACTIVE_INBOX, inboxId);
  },

  muteConversation: async ({ commit }, conversationId) => {
    // No try/catch: errors propagate so the caller can surface a failure toast
    // (e.g. backend 403 when a stale-session agent still sees the mute button).
    await ConversationApi.mute(conversationId);
    commit(types.MUTE_CONVERSATION);
  },

  unmuteConversation: async ({ commit }, conversationId) => {
    // No try/catch: errors propagate so the caller can surface a failure toast
    // (e.g. backend 403 when a stale-session agent still sees the unmute button).
    await ConversationApi.unmute(conversationId);
    commit(types.UNMUTE_CONVERSATION);
  },

  toggleEmailContinuity: async ({ commit }, conversationId) => {
    // eslint-disable-next-line no-useless-catch
    try {
      const { data } = await ConversationApi.toggleEmailContinuity({
        conversationId,
      });
      commit(types.TOGGLE_EMAIL_CONTINUITY, { data, conversationId });
      return data;
    } catch (error) {
      throw error;
    }
  },

  sendEmailTranscript: async (_, { conversationId, email }) => {
    try {
      await ConversationApi.sendEmailTranscript({ conversationId, email });
    } catch (error) {
      throw new Error(error);
    }
  },

  updateCustomAttributes: async (
    { commit },
    { conversationId, customAttributes }
  ) => {
    try {
      const response = await ConversationApi.updateCustomAttributes({
        conversationId,
        customAttributes,
      });
      const { custom_attributes } = response.data;
      commit(types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES, custom_attributes);
    } catch (error) {
      // Handle error
    }
  },

  setConversationFilters({ commit }, data) {
    commit(types.SET_CONVERSATION_FILTERS, data);
  },

  clearConversationFilters({ commit }) {
    commit(types.CLEAR_CONVERSATION_FILTERS);
  },

  setChatListFilters({ commit }, data) {
    commit(types.SET_CHAT_LIST_FILTERS, data);
  },

  updateChatListFilters({ commit }, data) {
    commit(types.UPDATE_CHAT_LIST_FILTERS, data);
  },

  assignPriority: async ({ dispatch }, { conversationId, priority }) => {
    try {
      await ConversationApi.togglePriority({
        conversationId,
        priority,
      });

      dispatch('setCurrentChatPriority', {
        priority,
        conversationId,
      });
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatPriority({ commit }, { priority, conversationId }) {
    commit(types.ASSIGN_PRIORITY, { priority, conversationId });
  },

  setContextMenuChatId({ commit }, chatId) {
    commit(types.SET_CONTEXT_MENU_CHAT_ID, chatId);
  },

  subscribeToConversation: async (
    { dispatch, state, rootGetters },
    {
      conversationId,
      pubsubToken = null,
      conversationOldestId = null,
      retryCount = 0,
    }
  ) => {
    const cableConnector = vueActionCable.getConnector();
    if (!cableConnector) return;

    const consumer = cableConnector.consumer;
    if (!consumer) return;

    try {
      const alreadySubscribed =
        state.subscribedNonAssignedConversations.includes(conversationId);

      if (!alreadySubscribed) {
        let confirmTimer = null;
        let rejectedSync = false;
        let confirmed = false;

        let subscription = null;
        let retried = false;

        const retrySubscription = () => {
          if (retried) return;
          retried = true;
          rejectedSync = true;
          if (confirmTimer) clearTimeout(confirmTimer);
          if (subscription) {
            subscription.unsubscribe();
          }
          dispatch('removeNonAssignedConversationSubscription', conversationId);
          if (retryCount < MAX_SUBSCRIPTION_RETRIES) {
            dispatch('subscribeToConversation', {
              conversationId,
              pubsubToken,
              retryCount: retryCount + 1,
            });
          }
        };

        subscription = consumer.subscriptions.create(
          {
            channel: 'ConversationChannel',
            conversation_id: conversationId,
            pubsub_token: pubsubToken,
            account_id: rootGetters.getCurrentAccountId,
          },
          {
            connected() {
              confirmed = true;
              if (confirmTimer) clearTimeout(confirmTimer);
            },
            rejected() {
              retrySubscription();
            },
            received: data => {
              cableConnector.onReceived(data);
            },
          }
        );

        // If neither connected nor rejected fires within the timeout,
        // the subscription is stuck — remove from state and retry.
        confirmTimer = setTimeout(() => {
          if (!confirmed) {
            retrySubscription();
          }
        }, SUBSCRIPTION_CONFIRM_TIMEOUT_MS);

        // If rejected synchronously, the retry already handled state — bail.
        if (rejectedSync) {
          if (confirmTimer) clearTimeout(confirmTimer);
          return;
        }

        // Only evict the oldest subscription when we actually created a new one.
        if (conversationOldestId !== null) {
          const oldestSubscription = consumer.subscriptions.subscriptions.find(
            s => {
              try {
                const identifier = JSON.parse(s.identifier);
                return (
                  identifier.channel === 'ConversationChannel' &&
                  String(identifier.conversation_id) ===
                    String(conversationOldestId)
                );
              } catch (e) {
                return false;
              }
            }
          );
          if (oldestSubscription) {
            oldestSubscription.unsubscribe();
          }
        }
      }

      dispatch('handleSubscribeNonAssignedConversation', conversationId);
    } catch (error) {
      // Handle error
    }
  },

  removeNonAssignedConversationSubscription({ commit }, conversationId) {
    commit(types.REMOVE_NON_ASSIGNED_CONVERSATION_SUBSCRIPTION, conversationId);
  },

  handleSubscribeNonAssignedConversation({ commit }, conversationId) {
    commit(types.HANDLE_SUBSCRIBE_NON_ASSIGNED_CONVERSATION, conversationId);
  },

  saveAudioTranscription: async (
    _,
    { conversationId, messageId, attachmentId, transcribedText }
  ) => {
    // Persistence is intentionally fire-and-forget (the transcription already shows
    // in the current session via Vuex). Capture failures in Sentry instead of
    // swallowing them, so we get visibility into the persistence failure rate.
    await MessageApi.saveAudioTranscription(
      conversationId,
      messageId,
      attachmentId,
      transcribedText
    ).catch(error => {
      Sentry.captureException(error);
    });
  },

  ...messageReadActions,
  ...messageTranslateActions,
};

export default actions;
